import { Alert, Badge, Box, Button, Card, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { IconAlertCircle, IconArrowBack, IconEye, IconHistory, IconPencil } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { TenantFeePolicyApi } from "../../../apis";
import type { FeePolicy, FeePolicyTier } from "../../../apis/tenantFeePolicy";
import { ConflictReloadModal } from "../../../components/billing/ConflictReloadModal";
import { FeePolicyStatusBadge } from "../../../components/billing/FeePolicyStatusBadge";
import { PermissionGate } from "../../../components/billing/PermissionGate";
import { PolicyCustomersSection } from "../../../components/billing/PolicyCustomersSection";
import { PolicyForm, type PolicyFormValue } from "../../../components/billing/PolicyForm";
import { useEditPolicy } from "../../../hooks/useEditPolicy";
import { useOptimisticMutation } from "../../../hooks/useOptimisticMutation";
import { formatDate } from "../../../utils/format";
import { scopeLabel } from "../../../utils/fee-policy-labels";
import FeePolicyDraftPage from "./draft";

const SCOPE_COLOR: Record<string, string> = { DEFAULT: "brand", NAMED: "cyan" };

/** Maps a loaded policy into the shared form shape (read-only render). */
function toFormValue(p: FeePolicy): PolicyFormValue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw: any = p;
  const tiers: FeePolicyTier[] | null =
    raw.tierConfig?.tiers ?? (Array.isArray(raw.tierConfig) ? raw.tierConfig : null);
  return {
    scope               : p.scope,
    name                : p.name,
    calculationMethod   : p.calculationMethod,
    feeRate             : p.feeRate,
    flatFeeAmount       : p.flatFeeAmount,
    tierConfig          : tiers,
    minFeeAmount        : p.minFeeAmount,
    maxFeeAmount        : p.maxFeeAmount,
    feeBasis            : p.feeBasis,
    deductionMode       : p.deductionMode,
    refundPolicy        : p.refundPolicy,
    absorbPlatformFee   : p.absorbPlatformFee,
    chargeEndCustomerFee: p.chargeEndCustomerFee ?? true,
    feeCurrency         : p.feeCurrency,
    applicableOrderTypes: p.applicableOrderTypes ?? [],
    applicableCurrencies: p.applicableCurrencies ?? [],
    excludedTradingRelationshipIds: p.excludedTradingRelationshipIds ?? [],
    effectiveUntil      : p.effectiveUntil ? String(p.effectiveUntil).substring(0, 10) : null,
  };
}

const FeePolicyDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get("edit") === "1";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, mutate, isLoading } = useSWR(`swr.my.fee-policy.${id}`, () => (id ? TenantFeePolicyApi.get(id) : null)) as any;
  const policy: FeePolicy | null = data ?? null;
  const [busy, setBusy] = useState(false);
  const { conflict, clearConflict, run } = useOptimisticMutation();
  const editPolicy = useEditPolicy();

  const formValue = useMemo(() => (policy ? toFormValue(policy) : null), [policy]);
  const effectiveFrom = policy?.effectiveFrom ? String(policy.effectiveFrom).substring(0, 10) : null;

  // Esc = back to list (spec §8). In edit mode the editor owns its own Esc handler.
  useEffect(() => {
    if (isEdit) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") navigate("/my-billing/fee-policy");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEdit, navigate]);

  // Edit mode reuses the full editor (same page, ?edit=1 toggle — spec §1/§3).
  if (isEdit) return <FeePolicyDraftPage />;

  if (isLoading || !policy || !formValue) {
    return (
      <Box p="lg">
        <Group gap="sm"><Loader size="sm" /><Text c="dimmed">Уншиж байна…</Text></Group>
      </Box>
    );
  }

  const act = async (fn: () => Promise<unknown>, label: string) => {
    setBusy(true);
    try {
      const res = await run(fn);
      if (res.ok) await mutate();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (e: any) { alert(`${label}: ${e?.message ?? e}`); }
    finally { setBusy(false); }
  };

  const isDraft     = policy.status === "DRAFT";
  const isScheduled = policy.status === "SCHEDULED";
  const isActive    = policy.status === "ACTIVE";
  const isSuspended = policy.status === "SUSPENDED";
  const isArchived  = policy.status === "ARCHIVED";
  const canEdit     = isDraft || isActive || isSuspended;
  const title       = policy.name ?? (policy.scope === "DEFAULT" ? "Үндсэн бодлого" : "(нэргүй)");

  return (
    <Box p="lg">
      {/* Header (spec §3.1): ← Буцах · нэр · scope/status/version */}
      <Group justify="space-between" align="center" mb="xs" wrap="wrap" gap="xs">
        <Group gap="sm" wrap="wrap">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowBack size={16} />}
            onClick={() => navigate(-1)}>
            Буцах
          </Button>
          <Title order={4}>{title}</Title>
          <Badge color={SCOPE_COLOR[policy.scope ?? ""] ?? "gray"} radius="sm">{scopeLabel(policy.scope)}</Badge>
          <FeePolicyStatusBadge status={policy.status} />
          {policy.currentVersion ? (
            <Text size="sm" c="dimmed" fw={600}>v{policy.currentVersion}</Text>
          ) : null}
        </Group>
      </Group>

      <Alert color="violet" variant="light" icon={<IconEye size={16} />} mb="md">
        Зөвхөн харах горим. Өөрчлөхийн тулд «Засах» товчийг дарна уу.
      </Alert>

      {isArchived && (
        <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />} mb="md">
          Архивласан бодлого. Засварлах боломжгүй.
        </Alert>
      )}

      <Stack gap="lg">
        <PolicyForm
          value={formValue}
          onChange={() => undefined}
          readonly
          effectiveFrom={effectiveFrom}
        />

        {policy.scope === "NAMED" && (isActive || isScheduled) && (
          <PolicyCustomersSection policyId={policy.id} />
        )}
        {policy.scope === "NAMED" && isDraft && (
          <Alert color="brand" variant="light" icon={<IconAlertCircle size={16} />}>
            Нэртэй бодлогод харилцагч оноох хэсэг бодлогыг идэвхжүүлсний дараа энд гарч ирнэ.
          </Alert>
        )}

        {policy.versions && policy.versions.length > 0 && (
          <Card withBorder padding="md">
            <Stack gap="xs">
              <Title order={5}>Хувилбарын түүх ({policy.versions.length})</Title>
              {policy.versions.map((v) => (
                <Group key={v.id} justify="space-between">
                  <Text>v{v.version}</Text>
                  <Text size="sm" c="dimmed">
                    {formatDate(v.effectiveFrom)} — {v.effectiveUntil ? formatDate(v.effectiveUntil) : "одоог хүртэл"}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Card>
        )}

        {/* View footer (spec §3.1): sticky bar so the actions stay reachable while scrolling. */}
        <Box
          pos="sticky"
          bottom={0}
          mt="xs"
          mx="calc(var(--mantine-spacing-lg) * -1)"
          mb="calc(var(--mantine-spacing-lg) * -1)"
          px="lg"
          py="md"
          style={{
            background: "var(--mantine-color-body)",
            borderTop: "1px solid var(--mantine-color-gray-3)",
            zIndex: 3,
          }}>
          <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="xs">
            {isActive && (
              <PermissionGate code="BIL_012">
                <Button variant="default" disabled={busy}
                  onClick={() => act(() => TenantFeePolicyApi.suspend(policy.id, policy.rowVersion), "Зогсооход алдаа")}>
                  Түр зогсоох
                </Button>
              </PermissionGate>
            )}
            {isSuspended && (
              <PermissionGate code="BIL_012">
                <Button color="teal" disabled={busy}
                  onClick={() => act(() => TenantFeePolicyApi.resume(policy.id, policy.rowVersion), "Сэргээхэд алдаа")}>
                  Сэргээх
                </Button>
              </PermissionGate>
            )}
            {isDraft && (
              <PermissionGate code="BIL_011">
                <Button color="red" variant="default" disabled={busy}
                  onClick={() => act(() => TenantFeePolicyApi.cancelDraft(policy.id, policy.rowVersion), "Цуцлах")}>
                  Ноорог цуцлах
                </Button>
              </PermissionGate>
            )}
            {isScheduled && (
              <PermissionGate code="BIL_011">
                <Button color="red" variant="default" disabled={busy}
                  onClick={() => act(() => TenantFeePolicyApi.cancelScheduled(policy.id, policy.rowVersion), "Цуцлах")}>
                  Товлосныг цуцлах
                </Button>
              </PermissionGate>
            )}
          </Group>

          <Group gap="xs">
            <Button variant="default" onClick={() => navigate("/my-billing/fee-policy")}>Хаах</Button>
            <Button variant="default" leftSection={<IconHistory size={16} />}
              onClick={() => navigate("/my-billing/fee-policy/history")}>
              Түүх
            </Button>
            {canEdit && (
              <PermissionGate code="BIL_011">
                <Button leftSection={<IconPencil size={16} />}
                  onClick={() => editPolicy(policy)}>
                  Засах
                </Button>
              </PermissionGate>
            )}
          </Group>
          </Group>
        </Box>
      </Stack>

      <ConflictReloadModal
        opened={conflict}
        onReload={() => { clearConflict(); mutate(); }}
      />
    </Box>
  );
};

export default FeePolicyDetailPage;
